/**
 * BrokerRenameModal Component
 *
 * Simple modal for renaming an existing broker.
 * Pre-fills with current name, validates non-empty.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';

// 2. Workspace packages
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Services & Types
import { brokerService } from '@/services/BrokerService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { Broker } from '@/types/schema/Broker';

/**
 * Props for BrokerRenameModal
 */
export interface BrokerRenameModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Broker to rename */
  broker: Broker;
  /** Callback after successful save */
  onSave: () => void;
}

/**
 * BrokerRenameModal Component
 */
export const BrokerRenameModal: React.FC<BrokerRenameModalProps> = ({
  isOpen,
  onClose,
  broker,
  onSave,
}) => {
  // Form state
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Toast notifications
  const { showToast } = useToastNotification();

  // Reset form when modal opens or broker changes
  useEffect(() => {
    if (isOpen) {
      setName(broker.name);
      setNameError(null);
      setSubmitError(null);
    }
  }, [isOpen, broker.name]);

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      return false;
    }
    setNameError(null);
    return true;
  };

  /**
   * Handle name change
   */
  const handleNameChange = (value: string): void => {
    setName(value);
    if (nameError) {
      setNameError(null);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (): Promise<void> => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    const trimmedName = name.trim();

    // Skip if name unchanged - just close without calling onSave
    if (trimmedName === broker.name) {
      onClose();
      return;
    }

    try {
      setIsSubmitting(true);

      await brokerService.update(broker.id, { name: trimmedName });
      syncCoordinatorService.notifyChange('brokers', broker.id);

      showToast({
        title: 'Broker Renamed',
        description: `Broker renamed to "${trimmedName}"`,
        color: 'success',
      });

      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to rename broker:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to rename broker';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" placement="top-center">
      <ModalContent>
        <ModalHeader>Rename Broker</ModalHeader>

        <ModalBody className="gap-4">
          {/* Name */}
          <Input
            label="Name"
            placeholder="Enter broker name"
            value={name}
            onValueChange={handleNameChange}
            isRequired
            isInvalid={!!nameError}
            errorMessage={nameError}
            autoFocus
            className="[&_input]:text-base"
          />

          {/* Error Alert */}
          {submitError && (
            <Alert color="danger" variant="flat">
              {submitError}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="primary" onPress={handleSubmit} isLoading={isSubmitting}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BrokerRenameModal;
